import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ChevronRight, FileSpreadsheet, Handshake, Play, Infinity as InfinityIcon, Building2, Globe2,
  Plus, Minus, Instagram, Youtube, Facebook, Mail, MessageCircle, UserPlus, UploadCloud, Rocket, MapPin,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { SplitsShowcaseMotion } from "@/components/SplitsShowcase";
import { InteractiveLogo } from "@/components/InteractiveLogo";
import { FlowLines } from "@/components/FlowLines";

const BG = "#FAFAFA", INK = "#171717", ACCENT = "#9E1B22";

// Decorative vertical gradient connector + glowing node between sections.
const SectionConnector = () => (
  <div className="flex justify-center" aria-hidden="true">
    <div className="relative h-16 w-px flow-line">
      <span className="absolute -bottom-1 left-1/2 h-3 w-3 -translate-x-1/2 rounded-full flow-node" />
    </div>
  </div>
);

const NAV = [
  { label: "Servicios", to: "#servicios" },
  { label: "Nosotros", to: "#nosotros" },
  { label: "Cómo funciona", to: "#pasos" },
  { label: "Fresh Finds", to: "/fresh-finds", highlight: true },
  { label: "FAQ", to: "#faq" },
];

const SOCIAL = {
  instagram: "https://www.instagram.com/alwaysfreshmusic/",
  youtube: "https://www.youtube.com/@Alwaysfreshmusicinc",
  facebook: "https://www.facebook.com/falwaysmusic",
  email: "admin@alwaysfreshmusic.com",
  whatsapp: "https://wa.me/584222002957",
  whatsappLabel: "+58 422 200 2957",
};

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, delay: d, ease: [0.16, 1, 0.3, 1] },
});

const VALUE = [
  { icon: Play, title: "Distribución Automatizada", text: "Envío directo a +150 tiendas globales (Spotify, Apple Music, TikTok, YouTube) con verificación automática de metadatos y portadas HD (3000×3000px)." },
  { icon: FileSpreadsheet, title: "Contabilidad Transparente", text: "Reportes financieros en tiempo real con exportación a Excel/CSV. Modelo claro del 70% neto para el catálogo." },
  { icon: Handshake, title: "Split Sheets Inteligentes", text: "División automática de porcentajes entre artistas y productores. Cada colaborador cobra de forma individual y autónoma." },
];

const TIMELINE = [
  { step: "01", tag: "2022 — Fundación", title: "Nace la visión", text: "Hugo Pernía funda Always Fresh Music para estructurar y profesionalizar la distribución musical independiente desde Venezuela." },
  { step: "02", tag: "Sede & Tecnología", title: "Infraestructura real", text: "Oficina física en Caracas y desarrollo del sistema de Split Sheets automáticos y contabilidad transparente 70/30." },
  { step: "03", tag: "+180M — Alcance Global", title: "Escala internacional", text: "Streams auditados e interacciones a escala global, con pitching a playlists oficiales en cada estreno." },
];

const STEPS = [
  { icon: UserPlus, n: "01", title: "Aplica y Configura", text: "Completa tu perfil, valida tus datos de cobro y accede a nuestro portal de distribución automatizado." },
  { icon: UploadCloud, n: "02", title: "Carga y Divide", text: "Sube tu audio en alta calidad, tu arte de portada y asigna los porcentajes de regalías a tus colaboradores en segundos." },
  { icon: Rocket, n: "03", title: "Distribuye y Cobra", text: "Lanzamos tu música en más de 150 plataformas globales y recibes reportes contables mensuales auditables." },
];

const FAQ = [
  { q: "¿Cómo funciona la comisión 70/30?", a: "Conservas el 100% de la propiedad intelectual y de tus másters. Retenemos un 30% únicamente sobre las regalías netas generadas, destinado a tecnología y distribución. El 70% restante es tuyo." },
  { q: "¿Cada cuánto y por qué medios recibo mis pagos?", a: "Hacemos cierres mensuales. Los retiros se procesan hacia Zelle, transferencias bancarias o PayPal." },
  { q: "¿Cuánto tarda un lanzamiento en aparecer?", a: "Recomendamos de 7 a 14 días de anticipación para garantizar la publicación a tiempo y los pitches a playlists oficiales." },
  { q: "¿Cómo funcionan los Split Sheets automáticos?", a: "Registras el correo de tus colaboradores y el sistema acredita automáticamente a cada cuenta su porcentaje correspondiente de regalías." },
];

const STORES = [
  { name: "Spotify", slug: "spotify", color: "#1DB954" },
  { name: "Apple Music", slug: "applemusic", color: "#FA243C" },
  { name: "Amazon Music", logo: "/amazon-music.svg", color: "#25D1DA" },
  { name: "YouTube Music", slug: "youtubemusic", color: "#FF0000" },
  { name: "Deezer", slug: "deezer", color: "#A238FF" },
  { name: "Tidal", slug: "tidal", color: "#00FFFF" },
  { name: "TikTok", slug: "tiktok", color: "#FE2C55" },
  { name: "Instagram", slug: "instagram", color: "#E4405F" },
  { name: "Facebook", slug: "facebook", color: "#1877F2" },
  { name: "SoundCloud", slug: "soundcloud", color: "#FF5500" },
  { name: "Boomplay", logo: "/boomplay.svg", color: "#F25C05" },
];

function Counter({ to, suffix = "", prefix = "", duration = 1800 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const tick = (now) => {
          const p = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setVal(Math.round(eased * to));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [to, duration]);
  return <span ref={ref} data-testid="counter-180m">{prefix}{val}{suffix}</span>;
}

const METRICS = [
  { render: () => <Counter to={180} prefix="+" suffix="M" />, title: "Streams globales", text: "Reproducciones acumuladas y auditadas en tiendas y plataformas de audio a nivel mundial." },
  { render: () => "Caracas", title: "Sede física", text: "Atención directa, soporte local y centro de operaciones para talentos independientes desde Venezuela." },
  { render: () => "70/30", title: "Modelo transparente", text: "Cobras el 70% de tus regalías netas, sin costos ocultos, mantenimientos ni cuotas anuales." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(0);
  const [hoveredStore, setHoveredStore] = useState(null);
  return (
    <div className="min-h-screen" style={{ backgroundColor: BG, color: INK }}>
      {/* Navbar */}
      <header className="sticky top-0 z-40 border-b backdrop-blur-md" style={{ borderColor: `${INK}1f`, backgroundColor: "rgba(255,255,255,0.86)" }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Link to="/" data-testid="landing-logo"><Logo imgClassName="h-11 w-auto object-contain" /></Link>
          <nav className="hidden items-center gap-8 md:flex">
            {NAV.map((n) => (
              <a key={n.to} href={n.to} data-testid={n.highlight ? "nav-fresh-finds" : undefined} className={`text-sm font-medium transition-opacity hover:opacity-60 ${n.highlight ? "rounded-full border px-3 py-1 font-semibold" : ""}`} style={{ color: n.highlight ? ACCENT : INK, borderColor: n.highlight ? ACCENT : undefined }}>{n.label}</a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button data-testid="nav-login-button" onClick={() => navigate("/login")} className="text-sm font-semibold transition-opacity hover:opacity-60" style={{ color: INK }}>Iniciar Sesión</button>
            <button data-testid="nav-register-button" onClick={() => navigate("/login?mode=register")} className="rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.03]" style={{ backgroundColor: ACCENT }}>Solicitar Distribución</button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <FlowLines className="absolute inset-0 h-full w-full opacity-70" data-testid="hero-flowlines" />
        <div className="relative z-10 mx-auto grid max-w-6xl items-center gap-10 px-5 py-20 lg:grid-cols-[1.35fr_0.65fr] lg:py-28">
          <div>
            <motion.div {...fade(0)} className="inline-flex items-center gap-2 rounded-full border px-3.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ borderColor: `${INK}26`, color: `${INK}99` }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: ACCENT }} /> Powered by SONA
            </motion.div>
            <motion.h1 {...fade(0.08)} className="mt-6 font-serif text-4xl leading-[1.05] tracking-tight sm:text-5xl lg:max-w-none" style={{ color: INK }}>
              Distribución de precisión e<br className="hidden lg:block" /> infraestructura contable<br className="hidden lg:block" /> automatizada.
            </motion.h1>
            <motion.p {...fade(0.16)} className="mt-5 max-w-md font-editorial text-base leading-relaxed lg:text-lg" style={{ color: `${INK}a6` }}>
              Lanza en +150 plataformas, divide regalías con Split Sheets y recibe reportes contables auditables cada mes.
            </motion.p>
            <motion.div {...fade(0.24)} className="mt-9 flex flex-wrap gap-4">
              <button data-testid="hero-portal-button" onClick={() => navigate("/login")} className="neu-btn group flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white" style={{ backgroundColor: ACCENT }}>
                Ingresar al Portal <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <a href="#pasos" className="neu-btn flex items-center gap-2 rounded-full border bg-[#FAFAFA] px-7 py-3.5 text-sm font-semibold hover:text-[#9E1B22]" style={{ borderColor: "#E5E5E5", color: INK }}>
                Cómo funciona
              </a>
            </motion.div>
          </div>

          {/* Interactive brand logo — cursor tilt + dynamic glow */}
          <InteractiveLogo />
        </div>

        <div className="relative z-10 mx-auto max-w-6xl px-5 pb-20">
          {/* Metric cards with animated counter */}
          <div className="grid gap-5 md:grid-cols-3" data-testid="metrics-row">
            {METRICS.map((m, i) => (
              <motion.div key={i} {...fade(0.1 + i * 0.1)} data-testid={`metric-card-${i}`} className="neu-raised bg-[#FAFAFA] p-7">
                <div className="font-serif text-5xl font-bold tracking-tight lg:text-6xl" style={{ color: ACCENT }}>{m.render()}</div>
                <div className="mt-3 font-serif text-xl font-bold" style={{ color: INK }}>{m.title}</div>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: `${INK}b3` }}>{m.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Value grid */}
      <section id="servicios" className="border-t" style={{ borderColor: `${INK}1f` }}>
        <div className="mx-auto max-w-6xl px-5 py-20">
          <motion.h2 {...fade(0)} className="font-serif text-4xl font-bold tracking-tight lg:text-5xl" style={{ color: INK }}>Una plataforma, todo tu negocio musical</motion.h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {VALUE.map((v, i) => (
              <motion.div key={v.title} {...fade(i * 0.1)} className="neu-raised bg-[#FAFAFA] p-7 transition-transform hover:-translate-y-1" data-testid={`value-card-${i}`}>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl text-[#FAFAFA]" style={{ backgroundColor: INK }}><v.icon className="h-5 w-5" strokeWidth={1.75} /></div>
                <h3 className="mt-5 font-serif text-xl font-bold tracking-tight" style={{ color: INK }}>{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: `${INK}b3` }}>{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sobre nosotros + Timeline — vinotinto section */}
      <section id="nosotros" className="sobre-nosotros-bg relative overflow-hidden border-t" style={{ borderColor: `${INK}1f` }} data-testid="sobre-nosotros-section">
        <div className="pointer-events-none absolute -left-32 bottom-0 h-[420px] w-[520px] rounded-full bg-white/60 blur-[120px]" />
        <div className="pointer-events-none absolute -right-24 top-0 h-[360px] w-[460px] rounded-full bg-[#CA0808]/40 blur-[120px]" />
        <div className="relative mx-auto max-w-6xl px-5 py-20">
          <motion.div {...fade(0)} className="max-w-3xl">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">Sobre nosotros</div>
            <h2 className="mt-3 font-serif text-4xl font-bold leading-tight tracking-tight text-white lg:text-5xl">
              Construyendo la infraestructura de distribución musical que Venezuela y Latinoamérica necesitan.
            </h2>
            <p className="mt-6 font-editorial text-xl leading-relaxed text-white/85">
              Fundada en 2022 por su CEO <strong className="text-white">Hugo Pernía</strong> —con más de 8 años de experiencia en la industria— para estructurar y profesionalizar el mercado independiente desde Venezuela, mediante tecnología y contabilidad automatizada.
            </p>
          </motion.div>

          <div className="relative mt-14 grid gap-6 md:grid-cols-3" data-testid="timeline">
            {/* Stepper flow line behind cards (desktop) */}
            <div className="absolute left-[16%] right-[16%] top-11 hidden border-t border-dashed border-white/30 md:block" />
            {TIMELINE.map((t, i) => (
              <motion.div key={t.step} {...fade(i * 0.12)} className="relative rounded-3xl bg-white p-7 shadow-[0_22px_48px_-18px_rgba(0,0,0,0.5),0_8px_18px_-10px_rgba(0,0,0,0.35)]" data-testid={`timeline-step-${i}`}>
                <div className="relative z-10 inline-flex font-serif text-5xl font-bold" style={{ color: ACCENT }}>{t.step}</div>
                <div className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: `${INK}99` }}>{t.tag}</div>
                <h3 className="mt-1 font-serif text-xl font-bold" style={{ color: INK }}>{t.title}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: `${INK}b3` }}>{t.text}</p>
                {i < TIMELINE.length - 1 && (
                  <span
                    className="absolute -right-6 top-1/2 z-20 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-[#E5E5E5] bg-white shadow-md md:flex"
                    aria-hidden="true"
                  >
                    <ChevronRight className="h-5 w-5" style={{ color: ACCENT }} strokeWidth={2.25} />
                  </span>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Modelo 70/30 */}
      <section id="modelo" className="border-t" style={{ borderColor: `${INK}1f` }}>
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-20 lg:grid-cols-2">
          <motion.div {...fade(0)}>
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: ACCENT }}>El modelo</div>
            <h2 className="mt-3 font-serif text-4xl font-bold tracking-tight lg:text-5xl" style={{ color: INK }}>Estructura 70/30. Transparencia total.</h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed" style={{ color: `${INK}b3` }}>
              El 70% neto generado por el catálogo va directo al proyecto y a sus creadores, con repartos automáticos por Split Sheet. Always Fresh gestiona el 30% restante por distribución, infraestructura y soporte global. Tú conservas el 100% de tus másters.
            </p>
          </motion.div>
          <motion.div {...fade(0.15)} className="flex gap-4">
            <div className="neu-dark flex-[0.6] rounded-2xl p-7 text-[#FAFAFA]" style={{ backgroundColor: INK }}>
              <div className="font-serif text-6xl font-bold tracking-tight tabular-nums">70%</div>
              <div className="mt-2 text-sm font-semibold">Catálogo & artistas</div>
              <div className="mt-1 text-xs opacity-70">Neto directo al proyecto</div>
            </div>
            <div className="neu-raised flex-[0.4] p-7" style={{ color: INK }}>
              <div className="font-serif text-5xl font-bold tracking-tight tabular-nums">30%</div>
              <div className="mt-2 text-sm font-semibold">Always Fresh</div>
              <div className="mt-1 text-xs opacity-70">Distribución & soporte</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Paso a paso */}
      <SectionConnector />
      <section id="pasos" className="border-t" style={{ borderColor: `${INK}1f` }}>
        <div className="mx-auto max-w-6xl px-5 py-20">
          <motion.h2 {...fade(0)} className="font-serif text-4xl font-bold tracking-tight lg:text-5xl" style={{ color: INK }}>Empieza en 3 pasos</motion.h2>
          <div className="relative mt-12 grid gap-6 md:grid-cols-3" data-testid="steps">
            <div className="absolute left-[8%] right-[8%] top-9 hidden h-0.5 rounded-full md:block" style={{ background: "linear-gradient(90deg, rgba(10,10,10,0), #0A0A0A 10%, #9E1B22 50%, #f3d9db 85%, rgba(255,255,255,0))" }} />
            {STEPS.map((s, i) => (
              <motion.div key={s.n} {...fade(i * 0.12)} className="neu-raised relative bg-[#FAFAFA] p-7" data-testid={`step-${i}`}>
                <div className="flow-node-ring relative z-10 flex h-14 w-14 items-center justify-center rounded-full text-[#FAFAFA]" style={{ backgroundColor: ACCENT }}><s.icon className="h-6 w-6" strokeWidth={1.75} /></div>
                <div className="mt-5 font-mono text-xs font-bold tracking-widest" style={{ color: `${INK}80` }}>PASO {s.n}</div>
                <h3 className="mt-1 font-serif text-xl font-bold" style={{ color: INK }}>{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: `${INK}b3` }}>{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Ecosistema / Stores */}
      <section id="distribucion" className="aura-bg aura-waves relative overflow-hidden border-t" style={{ borderColor: `${INK}1f` }} data-testid="distribucion-section">
        <div className="relative mx-auto max-w-6xl px-5 py-20">
          <motion.div {...fade(0)} className="glass-card sticker-peel relative overflow-hidden rounded-3xl p-8 text-[#FAFAFA] lg:p-14" style={{ "--peel": "72px" }}>
            <div className="relative text-center">
              <div className="neon-wine text-[11px] font-bold uppercase tracking-[0.4em]">Distribución</div>
              <h2 className="mt-4 font-serif text-4xl font-bold leading-[1.1] tracking-tight lg:text-6xl">Tu música, en todas partes.</h2>
              <p className="mt-4 text-sm text-white/70">Tus derechos, siempre tuyos. Lanzamientos ilimitados a +150 plataformas.</p>
            </div>
            <div className="relative mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {STORES.map((s, idx) => {
                const isH = hoveredStore === idx;
                return (
                  <div
                    key={s.name}
                    data-testid={`store-tile-${s.name}`}
                    onMouseEnter={() => setHoveredStore(idx)}
                    onMouseLeave={() => setHoveredStore(null)}
                    className="holo sticker-peel group flex flex-col items-center gap-3 overflow-hidden rounded-[20px] p-5 text-center"
                    style={{
                      "--peel": "26px",
                      background: isH ? `linear-gradient(160deg, ${s.color}33 0%, rgba(255,255,255,0.06) 60%)` : "linear-gradient(160deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.03) 100%)",
                      backdropFilter: "blur(10px)",
                      transform: isH ? "translateY(-6px) rotate(-1.5deg) scale(1.03)" : "translateY(0) rotate(0) scale(1)",
                      boxShadow: isH ? `0 22px 48px -14px ${s.color}, 0 0 28px -4px ${s.color}99` : "0 10px 30px -14px rgba(0,0,0,0.7)",
                      transition: "transform .35s cubic-bezier(.2,.8,.2,1), box-shadow .35s ease, background .35s ease",
                    }}
                  >
                    <span className="holo-sheen pointer-events-none absolute inset-0 rounded-[20px]" />
                    <span
                      className="relative flex h-14 w-14 items-center justify-center rounded-2xl"
                      style={{ background: `radial-gradient(circle at 30% 30%, ${s.color}55, ${s.color}14 70%)`, boxShadow: isH ? `0 0 32px 6px ${s.color}80` : `inset 0 1px 0 rgba(255,255,255,0.3)`, transition: "box-shadow .35s ease" }}
                    >
                      <img
                        src={s.logo || `https://cdn.simpleicons.org/${s.slug}/${s.color.slice(1)}`}
                        alt={s.name}
                        className="h-8 w-8 drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    </span>
                    <span className="relative text-xs font-semibold text-white/90">{s.name}</span>
                  </div>
                );
              })}
              <div className="holo sticker-peel flex flex-col items-center justify-center gap-2 overflow-hidden rounded-[20px] p-5 text-center" style={{ "--peel": "26px", background: "linear-gradient(160deg, rgba(202,8,8,0.45) 0%, rgba(74,8,12,0.5) 100%)", boxShadow: "0 0 36px -6px rgba(202,8,8,0.8)" }}>
                <span className="neon-wine font-serif text-2xl font-bold">+150</span>
                <span className="text-xs font-semibold uppercase tracking-wider text-white/80">Más tiendas</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Splits & Publishing */}
      <section id="splits" className="border-t" style={{ borderColor: `${INK}1f` }} data-testid="splits-section">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <SplitsShowcaseMotion onCta={() => navigate("/login?mode=register")} />
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t" style={{ borderColor: `${INK}1f` }}>
        <div className="mx-auto max-w-3xl px-5 py-20">
          <motion.h2 {...fade(0)} className="text-center font-serif text-4xl font-bold tracking-tight lg:text-5xl" style={{ color: INK }}>Preguntas frecuentes</motion.h2>
          <div className="mt-10 space-y-3" data-testid="faq">
            {FAQ.map((f, i) => {
              const isOpen = open === i;
              return (
                <div key={i} className="neu-raised-sm overflow-hidden bg-[#FAFAFA]" data-testid={`faq-item-${i}`}>
                  <button data-testid={`faq-toggle-${i}`} onClick={() => setOpen(isOpen ? -1 : i)} className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left">
                    <span className="font-serif text-lg font-bold" style={{ color: INK }}>{f.q}</span>
                    {isOpen ? <Minus className="h-5 w-5 shrink-0" style={{ color: ACCENT }} /> : <Plus className="h-5 w-5 shrink-0" style={{ color: ACCENT }} />}
                  </button>
                  {isOpen && <div className="px-6 pb-5 text-sm leading-relaxed" style={{ color: `${INK}cc` }} data-testid={`faq-answer-${i}`}>{f.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="border-t" style={{ borderColor: `${INK}1f` }}>
        <div className="mx-auto max-w-6xl px-5 py-20 text-center">
          <motion.h2 {...fade(0)} className="font-serif text-4xl font-bold tracking-tight lg:text-5xl" style={{ color: INK }}>¿Listo para distribuir con precisión?</motion.h2>
          <motion.button {...fade(0.1)} data-testid="cta-register-button" onClick={() => navigate("/login?mode=register")} className="mt-8 inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-semibold text-white transition-transform hover:scale-[1.03]" style={{ backgroundColor: ACCENT }}>
            Solicitar Distribución <ArrowRight className="h-4 w-4" />
          </motion.button>
        </div>
      </section>

      {/* Footer */}
      <footer id="soporte" className="border-t text-[#FAFAFA]" style={{ backgroundColor: INK, borderColor: "#E5E5E5" }}>
        <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Logo imgClassName="h-10 w-auto object-contain" variant="white" />
            <p className="mt-4 text-sm leading-relaxed opacity-70">Infraestructura de distribución y contabilidad musical. Caracas, Venezuela.</p>
            <span className="mt-4 inline-block rounded-full border border-white/20 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] opacity-70">Plataforma v1.0 · Powered by SONA</span>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] opacity-60">Navegación</div>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><a href="#servicios" className="opacity-80 transition-opacity hover:opacity-100">Servicios</a></li>
              <li><button onClick={() => navigate("/login")} className="opacity-80 transition-opacity hover:opacity-100">Portal de Artistas</button></li>
              <li><button onClick={() => navigate("/login?mode=register")} className="opacity-80 transition-opacity hover:opacity-100">Solicitar Distribución</button></li>
              <li><a href={`mailto:${SOCIAL.email}`} className="opacity-80 transition-opacity hover:opacity-100">Soporte Técnico</a></li>
            </ul>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] opacity-60">Legales</div>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><Link to="/legal/terminos" className="opacity-80 transition-opacity hover:opacity-100">Términos de Servicio</Link></li>
              <li><Link to="/legal/privacidad" className="opacity-80 transition-opacity hover:opacity-100">Política de Privacidad</Link></li>
              <li><Link to="/legal/distribucion" className="opacity-80 transition-opacity hover:opacity-100">Términos de Distribución Digital</Link></li>
              <li><Link to="/legal/royalties" className="opacity-80 transition-opacity hover:opacity-100">Políticas de Regalías</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] opacity-60">Contacto</div>
            <div className="mt-4 flex gap-3">
              <a href={SOCIAL.instagram} target="_blank" rel="noreferrer" data-testid="social-instagram" aria-label="Instagram" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 transition-colors hover:bg-white/10"><Instagram className="h-4 w-4" /></a>
              <a href={SOCIAL.youtube} target="_blank" rel="noreferrer" data-testid="social-youtube" aria-label="YouTube" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 transition-colors hover:bg-white/10"><Youtube className="h-4 w-4" /></a>
              <a href={SOCIAL.facebook} target="_blank" rel="noreferrer" data-testid="social-facebook" aria-label="Facebook" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 transition-colors hover:bg-white/10"><Facebook className="h-4 w-4" /></a>
              <a href={SOCIAL.whatsapp} target="_blank" rel="noreferrer" data-testid="social-whatsapp" aria-label="WhatsApp" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 transition-colors hover:bg-white/10"><MessageCircle className="h-4 w-4" /></a>
              <a href={`mailto:${SOCIAL.email}`} data-testid="social-email" aria-label="Email" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 transition-colors hover:bg-white/10"><Mail className="h-4 w-4" /></a>
            </div>
            <a href={SOCIAL.whatsapp} target="_blank" rel="noreferrer" className="mt-4 flex items-center gap-2 text-sm opacity-80 transition-opacity hover:opacity-100"><MessageCircle className="h-4 w-4" /> {SOCIAL.whatsappLabel}</a>
            <a href={`mailto:${SOCIAL.email}`} className="mt-2 flex items-center gap-2 text-sm opacity-80 transition-opacity hover:opacity-100"><Mail className="h-4 w-4" /> {SOCIAL.email}</a>
            <div className="mt-2 flex items-center gap-2 text-sm opacity-60"><MapPin className="h-4 w-4" /> Caracas, Venezuela</div>
          </div>
        </div>
        <div className="border-t border-white/10 py-5 text-center text-xs opacity-60">Always Fresh Music © 2026 · Todos los derechos reservados.</div>
      </footer>
    </div>
  );
}
